import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import BasePaper from "components/Base/BasePaper";
import { RectangularTextButton, RoundIconButton } from "components/Base/Buttons";

interface FieldConfig {
  name: string;
  label: string;
  type?: "text" | "password" | "email" | "number" | "multiline";
  rows?: number;
  required?: boolean;
}

interface AuthenticationFormProps {
  title: string;
  fields: FieldConfig[];
  initialValues?: Record<string, any>;
  error?: Record<string, string> | null;
  submitLabel?: string;
  onSubmit: (values: Record<string, any>) => Promise<void> | void;
  backLabel?: string;
  onBack?: () => void;
  extraContent?: React.ReactNode;
}

export default function AuthenticationForm({
  title,
  fields,
  initialValues = {},
  error,
  submitLabel = "Continue",
  onSubmit,
  backLabel = "Back",
  onBack,
  extraContent,
}: AuthenticationFormProps) {
  const [values, setValues] = useState<Record<string, any>>(initialValues);
  const [showPasswordField, setShowPasswordField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function updateField(name: string, value: any) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  function togglePasswordVisibility(name: string) {
    setShowPasswordField((prev) => (prev === name ? null : name));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ mt: 6, display: "flex", justifyContent: "center", px: 2 }}>
      <BasePaper>
        <form onSubmit={handleSubmit} noValidate>
          <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
            {title}
          </Typography>

          {error?._form && (
            <Typography color="error" sx={{ mb: 2, textAlign: "center" }}>
              {error._form}
            </Typography>
          )}

          {fields.map((field) => {
            const isPassword = field.type === "password";
            const isMultiline = field.type === "multiline";

            return (
              <TextField
                key={field.name}
                label={`${field.label}`}
                fullWidth
                margin="normal"
                type={
                  isPassword
                    ? showPasswordField === field.name
                      ? "text"
                      : "password"
                    : field.type || "text"
                }
                multiline={isMultiline}
                rows={isMultiline ? field.rows || 3 : undefined}
                value={values[field.name] ?? ""}
                onChange={(e) => updateField(field.name, e.target.value)}
                required={field.required}
                error={Boolean(error?.[field.name])}
                helperText={error?.[field.name]}
                InputProps={{
                  endAdornment: isPassword ? (
                    <InputAdornment position="end">
                      <RoundIconButton
                        colour="clear"
                        displaySize="inline"
                        icon={
                          showPasswordField === field.name ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )
                        }
                        onClick={() => togglePasswordVisibility(field.name)}
                        edge="end"
                      />
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            );
          })}

          {extraContent && <Box sx={{ mt: 2 }}>{extraContent}</Box>}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            {onBack && (
              <RectangularTextButton
                colour="clear"
                onClick={onBack}
                disabled={submitting}
              >
                {backLabel}
              </RectangularTextButton>
            )}

            <RectangularTextButton
              colour="highlighted"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : submitLabel}
            </RectangularTextButton>
          </Box>
        </form>
      </BasePaper>
    </Box>
  );
}